// src/stores/app.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import app from 'configs/appResolver';
import { clone } from 'utils/helpers';
import { getStorageAuthItem } from './auth';
import { logErrorSilent } from 'utils/errorLogger';
import * as sseParametersManager from 'services/sseParametersManager';
import { API } from 'constants/Api';
import consoleX from 'utils/consoleX';
import useZustand from 'hooks/useZustand';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************

const LOCAL_STORAGE_KEY = 'storage-app';
const FILE_TITLE = 'APP STORE';

// *******************************************************************************
// STATE
// *******************************************************************************
export const initialState = {
  appActivated: false,
  routes: null,
  currentRoute: null,
  requesting: 0,
  parameters: null,
  parametersLoading: false,
  parametersConnected: false, // SSE connection status
  appModules: [],
  navigationMenu: [],
  state: {}, // global dynamic state
};

// *******************************************************************************
// METHODS
// *******************************************************************************
function buildStorageKey() {
  return `${app.ID}:${LOCAL_STORAGE_KEY}`;
}

export function getStorageAppItem(key) {
  const field = buildStorageKey();
  let lsData;
  try {
    lsData = JSON.parse(localStorage.getItem(field));
  } catch (e) {
    logErrorSilent(e, {
      context: 'appStore.getStorageAppItem (JSON.parse)',
      extraData: { field, key },
    });
  }
  return lsData?.[key];
}

export function setStorageAppItem(key, value) {
  const field = buildStorageKey();
  let lsData = {};
  try {
    lsData = JSON.parse(localStorage.getItem(field)) || {};
  } catch (e) {
    lsData = {};
  }
  if (value) {
    lsData[key] = value;
  } else {
    delete lsData[key];
  }
  localStorage.setItem(field, JSON.stringify(lsData));
}

export function removeStorageAppItem(key) {
  const field = buildStorageKey();
  let lsData = {};
  try {
    lsData = JSON.parse(localStorage.getItem(field)) || {};
  } catch (e) {
    lsData = {};
  }
  if (lsData.hasOwnProperty(key)) {
    delete lsData[key];
    localStorage.setItem(field, JSON.stringify(lsData));
  }
}

// *******************************************************************************
// STORE
// *******************************************************************************
const store = create(
  devtools(
    (set, get, api) => {
      // initialize async fns and first-load controller
      setTimeout(() => {
        get().initFunctions();
      }, 0);

      return {
        ...initialState,

        // ─────────────────────────────────────────────────────────────────────────
        // ROUTING & NAV
        // ─────────────────────────────────────────────────────────────────────────
        setRoutes: value => set({ routes: value }),
        setCurrentRoute: value => set({ currentRoute: value }),
        setNavigationMenu: value => set({ navigationMenu: value }),

        // ─────────────────────────────────────────────────────────────────────────
        // APP FLAGS
        // ─────────────────────────────────────────────────────────────────────────
        setRequesting: value => set({ requesting: value }),
        setAppActivated: value => set({ appActivated: value }),
        setAppModules: value => set({ appModules: value }),
        setParameters: value => set({ parameters: value }),

        setState: (state = {}) => {
          const currentState = get().state || {};
          set({ state: { ...currentState, ...state } });
        },

        setShowPaymentModal: value => {
          const jPayments = get().jarvislyPayment || {};
          set({ ...jPayments, showModal: value });
        },

        // ─────────────────────────────────────────────────────────────────────────
        // REMOTE PARAMS (SSE-based)
        // ─────────────────────────────────────────────────────────────────────────
        initFunctions: async afterInitCallback => {
          get().setState({
            // Initialize SSE connection for real-time parameter updates
            // Automatically includes accountId (required) and subscriptionId (optional) from auth store
            initSSE: async (filters = {}) => {
              const token = getStorageAuthItem('token');
              if (!token) {
                console.warn('[App Store] Cannot init SSE: no authentication token');
                return;
              }

              // Get accountId and subscriptionId from Zustand auth store
              // accountId (user._id) is required for application scope parameters (theme, preferences)
              // subscriptionId is optional - when present, also loads module scope parameters
              const user = useZustand('auth', 'user', false);
              const subscription = useZustand('auth', 'subscription', false);
              const accountId = filters.accountId || user?._id;
              const subscriptionId = filters.subscriptionId || subscription?._id;

              // Merge auth IDs with any explicit filters
              const sseFilters = {
                ...filters,
                accountId,
                subscriptionId,
              };

              consoleX({
                message: 'Initializing SSE with Auth Context',
                emoji: '🔐',
                flag: 'i',
                title: FILE_TITLE,
                info: {
                  'Account ID': sseFilters.accountId ? `${sseFilters.accountId.slice(0, 8)}...` : 'Not Set',
                  'Subscription ID': sseFilters.subscriptionId ? `${sseFilters.subscriptionId.slice(0, 8)}...` : 'Not Set (First Access)',
                  'Explicit Filters': Object.keys(filters).length > 0 ? filters : 'None',
                },
              });

              set({ parametersLoading: true, parametersConnected: false });

              sseParametersManager.initSSE(
                // onUpdate callback
                (data, type) => {
                  if (type === 'initial') {
                    // Initial load: data is full parameters object
                    const appParameters = clone(data);
                    set({ parametersLoading: false, parameters: appParameters });

                    // Apply theme preferences from backend (category: 'appearance')
                    // Even if undefined, we need to call applyThemePreferences to sync localStorage
                    const themePreferences = appParameters?.application?.appearance || {};

                    import('./theme').then(async ({ useThemeStore }) => {
                      const applyThemePreferences =
                        useThemeStore.getState().applyThemePreferences;
                      if (applyThemePreferences) {
                        try {
                          await applyThemePreferences(themePreferences);
                        } catch (err) {
                          console.error('[Theme] Error applying theme preferences:', err);
                        }
                      }
                    });
                  } else if (type === 'update') {
                    // Incremental update: data is { scope, moduleId, category, key, value }
                    const currentParams = get().parameters || { application: {}, modules: {} };
                    const updatedParams = clone(currentParams);

                    if (data.scope === 'application') {
                      // Update application parameter
                      if (!updatedParams.application) updatedParams.application = {};
                      if (!updatedParams.application[data.category])
                        updatedParams.application[data.category] = {};
                      updatedParams.application[data.category][data.key] = data.value;

                      // Apply theme preferences if category is 'appearance'
                      if (data.category === 'appearance') {
                        const themePrefs = updatedParams.application.appearance || {};
                        import('./theme').then(async ({ useThemeStore }) => {
                          const applyThemePreferences =
                            useThemeStore.getState().applyThemePreferences;
                          if (applyThemePreferences) {
                            try {
                              await applyThemePreferences(themePrefs);
                            } catch (err) {
                              console.error('[Theme] Error applying theme preferences:', err);
                            }
                          }
                        });
                      }
                    } else if (data.scope === 'module') {
                      // Update module parameter
                      if (!updatedParams.modules) updatedParams.modules = {};
                      if (!updatedParams.modules[data.moduleId])
                        updatedParams.modules[data.moduleId] = {};

                      // When category === key, place value directly (e.g., units: [...])
                      // Otherwise nest under category.key (e.g., table.init: {...})
                      if (data.category === data.key) {
                        updatedParams.modules[data.moduleId][data.category] = data.value;
                      } else {
                        if (!updatedParams.modules[data.moduleId][data.category])
                          updatedParams.modules[data.moduleId][data.category] = {};
                        updatedParams.modules[data.moduleId][data.category][data.key] = data.value;
                      }
                    }

                    set({ parametersLoading: false, parameters: updatedParams });
                  }
                },
                // onConnect callback
                connectionInfo => {
                  set({ parametersConnected: true });
                },
                // onError callback
                error => {
                  set({ parametersLoading: false, parametersConnected: false });
                },
                // filters (with accountId and subscriptionId from auth)
                sseFilters
              );
            },

            // Close SSE connection
            closeSSE: () => {
              sseParametersManager.closeSSE();
              set({ parametersConnected: false });
            },

            // Force reconnection (useful for token refresh)
            reconnectSSE: (filters = {}) => {
              sseParametersManager.reconnect(filters);
            },

            // Refresh parameters via REST API (fallback when SSE is not connected)
            refreshParameters: async () => {
              try {
                const { default: apiConfig } = await import('configs/fetchInterceptor');
                const response = await apiConfig.get(API.PARAMETERS.GET);

                if (response?.data?.parameters) {
                  const appParameters = clone(response.data.parameters);
                  set({ parameters: appParameters });

                  consoleX({
                    message: 'Parameters Refreshed via API',
                    emoji: '🔄',
                    flag: 's',
                    title: 'APP STORE',
                    info: {
                      'Modules Count': Object.keys(appParameters?.modules || {}).length,
                      'Application Keys': Object.keys(appParameters?.application || {}),
                    },
                  });

                  return appParameters;
                }
              } catch (err) {
                consoleX({
                  message: 'Failed to Refresh Parameters via API',
                  emoji: '❌',
                  flag: 'e',
                  title: 'APP STORE',
                  info: { Error: err.message },
                });
                return null;
              }
            },
          });

          if (typeof afterInitCallback === 'function') {
            afterInitCallback();
          }
        },
      };
    },
    { name: 'appStore', enabled: true }
  )
);

// *******************************************************************************
// EXPORTS
// *******************************************************************************
export const useAppStore = store;
// export const appStore = store.getState;
// export const fromAppStore = sel => sel(appStore());

// *******************************************************************************
// SELECTORS (For non-React contexts)
// *******************************************************************************
export const fromAppStore = selector => {
  if (typeof selector === 'function') {
    return selector(useAppStore.getState());
  }
  return useAppStore.getState();
};

// *******************************************************************************
// HELPERS
// *******************************************************************************
export const updatePageQuery = (moduleId, pageName, newQueryPart) => {
  const { appModules, setAppModules } = appStore();
  if (!appModules?.[moduleId]?.pages?.[pageName]?.query) return;

  const currentQuery = appModules[moduleId].pages[pageName].query;

  const updatedModules = {
    ...appModules,
    [moduleId]: {
      ...appModules[moduleId],
      pages: {
        ...appModules[moduleId].pages,
        [pageName]: {
          ...appModules[moduleId].pages[pageName],
          query: {
            ...currentQuery,
            ...newQueryPart,
          },
        },
      },
    },
  };

  setAppModules(updatedModules);
};
